import { Component, EventEmitter, Input, Output, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { CameraDto, PointDirection } from '../../../../../api/gen';

const DEFAULT_LANE_HANDOVER_MINUTES = 5;

/** `<input type="time">` gives "HH:mm"; the API's TimeOnly takes "HH:mm:ss". */
function toTimeOnly(value: string | null | undefined): string | null {
    return value ? `${value.slice(0, 5)}:00` : null;
}

@Component({
    selector: 'app-create-detection-point-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressBarModule,
        MatChipsModule
    ],
    templateUrl: './create-detection-point-form.component.html',
    styleUrl: './create-detection-point-form.component.scss'
})
export class CreateDetectionPointFormComponent {
    @Input() isProcessing = false;
    @Input() cameras: CameraDto[] | null | undefined = [];
    @Output() createDetectionPoint = new EventEmitter<any>();
    @Output() cancel = new EventEmitter<void>();

    detectionPointForm!: FormGroup;
    directions = [PointDirection.Approach, PointDirection.Departure, PointDirection.Both, PointDirection.Unknown];
    selectedCameraName = signal<string>('');

    constructor(private fb: FormBuilder) {
        this.initForm();

        // Update camera name when selection changes
        effect(() => {
            const selectedCameraId = this.detectionPointForm.get('cameraId')?.value;
            const camera = this.cameras?.find(c => c.id === selectedCameraId);
            this.selectedCameraName.set(camera?.deviceName || '');
        });
    }

    private initForm(): void {
        this.detectionPointForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2)]],
            cameraId: ['', Validators.required],
            expectedDirections: [PointDirection.Approach, Validators.required],
            description: [''],
            isActive: [true],
            // Reversible lane (one barrier: entry part of the day, exit the rest). Only for Both points.
            isReversible: [false],
            laneEntryFrom: ['18:00'],
            laneExitFrom: ['12:00'],
            laneHandoverMinutes: [DEFAULT_LANE_HANDOVER_MINUTES, [Validators.min(2)]]
        });
    }

    /** A lane schedule needs a point that knows which way cars move (one camera for both, or one per side). */
    get canBeReversible(): boolean {
        const direction = this.detectionPointForm.get('expectedDirections')?.value;
        return !!direction && direction !== PointDirection.Unknown;
    }

    get isReversible(): boolean {
        return this.canBeReversible && !!this.detectionPointForm.get('isReversible')?.value;
    }

    onSubmit(): void {
        if (this.detectionPointForm.valid) {
            const { isReversible, laneEntryFrom, laneExitFrom, laneHandoverMinutes, ...point } = this.detectionPointForm.value;
            this.createDetectionPoint.emit(this.isReversible
                ? {
                    ...point,
                    laneEntryFrom: toTimeOnly(laneEntryFrom),
                    laneExitFrom: toTimeOnly(laneExitFrom),
                    laneHandoverMinutes
                }
                : point);
        }
    }

    onCancel(): void {
        this.detectionPointForm.reset({
            isActive: true,
            expectedDirections: ['Approach'],
            isReversible: false,
            laneEntryFrom: '18:00',
            laneExitFrom: '12:00',
            laneHandoverMinutes: DEFAULT_LANE_HANDOVER_MINUTES
        });
        this.cancel.emit();
    }


    getDirectionIcon(direction: PointDirection): string {
        const iconMap: Record<PointDirection, string> = {
            [PointDirection.Approach]: 'arrow_forward',
            [PointDirection.Departure]: 'arrow_back',
            [PointDirection.Both]: 'unfold_more',
            [PointDirection.Unknown]: 'help'
        };
        return iconMap[direction] || 'help';
    }

    getDirectionEmoji(direction: PointDirection): string {
        const emojiMap: Record<PointDirection, string> = {
            [PointDirection.Approach]: '➡️',
            [PointDirection.Departure]: '⬅️',
            [PointDirection.Both]: '↔️',
            [PointDirection.Unknown]: '❓'
        };
        return emojiMap[direction] || '❓';
    }

    trackByDirection(index: number, direction: PointDirection): string {
        return direction;
    }
}
